import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UseCandidate from 'cms/features/feature_job/hooks/UseCandidate';
import {
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Avatar,
    Chip,
    Stack,
    Divider,
    Card,
    CardContent,
    Alert,
    Button,
    CircularProgress
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import CandidateSkills from 'features/feature_job/components/candidate/CandidateSkills';
import CandidateExperience from 'features/feature_job/components/candidate/CandidateExperience';
import CandidateEducation from 'features/feature_job/components/candidate/CandidateEducation';
import CandidateCertificates from 'features/feature_job/components/candidate/CandidateCertificates';
import CandidateLanguages from 'features/feature_job/components/candidate/CandidateLanguages';
import CandidateReferences from 'features/feature_job/components/candidate/CandidateReferences';
import { CVHttpService } from 'features/feature_cv/services';

function EmployerCandidateDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: candidate, isLoading, isError } = UseCandidate(id);

    const handleDownloadCV = async () => {
        if (!candidate?.cvs || candidate.cvs.length === 0) {
            alert('No CV available for this candidate');
            return;
        }
        try {
            const cvId = candidate.cvs[0].id;
            await CVHttpService.downloadPDF(cvId, 'style2');
        } catch (err) {
            alert('Failed to download CV');
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !candidate) {
        return (
            <Box>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/employer/candidates')}
                    sx={{ mb: 3 }}
                >
                    Back to Candidates
                </Button>
                <Alert severity="error">Error loading candidate details</Alert>
            </Box>
        );
    }

    const profile = candidate.profile;
    const displayName = getProfileDisplayName(profile);
    const avatarSrc = getProfileAvatarUrl(profile);

    return (
        <Box>
            {/* Back Button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/employer/candidates')}
                sx={{ mb: 3 }}
            >
                Back to Candidates
            </Button>

            <Grid container spacing={3}>
                {/* Main Content */}
                <Grid item xs={12} md={8}>
                    {/* Profile Header */}
                    <Paper 
                        elevation={0}
                        sx={{ 
                            p: 4, 
                            mb: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2
                        }}
                    >
                        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                            <Avatar
                                src={avatarSrc || undefined}
                                alt={displayName}
                                sx={{ 
                                    width: 120, 
                                    height: 120,
                                    border: '3px solid',
                                    borderColor: 'divider',
                                    fontSize: '2.5rem',
                                    fontWeight: 700,
                                    bgcolor: nameToAvatarColor(displayName),
                                }}
                            >
                                {getProfileAvatarLetter(profile)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Typography variant="h4" fontWeight={700} gutterBottom>
                                    {candidate.profile?.first_name || ''} {candidate.profile?.last_name || ''}
                                </Typography>
                                {candidate.profile?.title && (
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        {candidate.profile.title}
                                    </Typography>
                                )}
                                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2, gap: 1 }}>
                                    {candidate.profile?.city && (
                                        <Chip
                                            icon={<LocationOnIcon />}
                                            label={candidate.profile.city}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                    {candidate.profile?.title && (
                                        <Chip
                                            icon={<WorkIcon />}
                                            label={candidate.profile.title}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                    {candidate.experience && (
                                        <Chip
                                            icon={<AccessTimeIcon />}
                                            label={candidate.experience}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    </Paper>

                    {/* About Section */}
                    {candidate.profile?.bio && (
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 3, 
                                mb: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                About
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                                {candidate.profile.bio}
                            </Typography>
                        </Paper>
                    )}

                    {/* Skills */}
                    <CandidateSkills skills={candidate.skills} />

                    {/* Experience */}
                    <CandidateExperience experiences={candidate.experiences} />

                    {/* Education */}
                    <CandidateEducation education={candidate.education} />

                    {/* Certificates */}
                    <CandidateCertificates certificates={candidate.certificates} />

                    {/* Languages */}
                    <CandidateLanguages languages={candidate.languages} />

                    {/* References */}
                    <CandidateReferences references={candidate.references} />
                </Grid>

                {/* Sidebar */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        {/* Contact Information */}
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Contact Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={2}>
                                {candidate.profile?.email && (
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <EmailIcon fontSize="small" color="action" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Email
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {candidate.profile.email}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                )}
                                {candidate.profile?.phone && (
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PhoneIcon fontSize="small" color="action" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Phone
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {candidate.profile.phone}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                )}
                                {candidate.profile?.website && (
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LanguageIcon fontSize="small" color="action" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Website
                                                </Typography>
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight={500}
                                                    component="a"
                                                    href={candidate.profile.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ 
                                                        color: 'primary.main',
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                >
                                                    {candidate.profile.website}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>

                        {/* Additional Information */}
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Additional Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            {/* Availability */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Availability
                                </Typography>
                                <Chip
                                    label={candidate.is_available ? 'Available for Work' : 'Not Available'}
                                    color={candidate.is_available ? 'success' : 'warning'}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                />
                            </Box>

                            {/* Preferred Job Types */}
                            {candidate.preferred_job_types?.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Preferred Job Types
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
                                        {candidate.preferred_job_types.map(type => (
                                            <Chip
                                                key={type.id}
                                                label={type.title}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Paper>

                        {/* Actions */}
                        <Card
                            elevation={0}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2
                            }}
                        >
                            <CardContent>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Actions
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Stack spacing={2}>
                                    {candidate.profile?.email && (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<EmailIcon />}
                                            href={`mailto:${candidate.profile.email}`}
                                        >
                                            Send Email
                                        </Button>
                                    )}
                                    {candidate?.cvs && candidate.cvs.length > 0 && (
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadCV}
                                        >
                                            Download CV
                                        </Button>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}

export default EmployerCandidateDetailsPage;
